/**
 * Service Worker 註冊模組
 *
 * 基於 CRA cra-template-pwa-typescript 標準模式，
 * 使用 workbox-window 管理 SW 生命週期與版本更新偵測。
 *
 * 僅在 production build 中啟用。
 */

import { Workbox } from 'workbox-window';

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration, wb: Workbox) => void;
};

function isLocalhost(): boolean {
  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    /^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/.test(
      window.location.hostname
    )
  );
}

export function register(config?: Config): void {
  if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) {
    return;
  }

  const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
  if (publicUrl.origin !== window.location.origin) {
    return;
  }

  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

    if (isLocalhost()) {
      checkValidServiceWorker(swUrl, config);
      navigator.serviceWorker.ready.then(() => {
        console.log('[SW] 應用程式由 Service Worker 快取提供服務（localhost）。');
      });
    } else {
      registerValidSW(swUrl, config);
    }
  });
}

function registerValidSW(swUrl: string, config?: Config): void {
  // updateViaCache: 'none' → 瀏覽器檢查更新時「不使用 HTTP 快取」重抓 service-worker.js，
  // 確保部署後能立即偵測到新版（否則舊 SW 被快取，需手動 Ctrl+Shift+R 才更新）。
  const wb = new Workbox(swUrl, { updateViaCache: 'none' });

  // 偵測到新版本：SW 已安裝但在 waiting 狀態
  wb.addEventListener('waiting', () => {
    console.log('[SW] 偵測到新版本，等待使用者確認更新...');
    if (config?.onUpdate) {
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration) {
          config.onUpdate!(registration, wb);
        }
      });
    }
  });

  let _reloaded = false;
  wb.addEventListener('controlling', (event: any) => {
    console.log('[SW] Service Worker 已接管頁面控制權。');
    // 更新版接管時「自動重載一次」載入新版（保留當前 URL）——使用者免再手動清快取。
    // （event.isUpdate 為真＝更新而非首裝，故首次安裝不會重載。）
    if (event && event.isUpdate && !_reloaded) {
      _reloaded = true;
      window.location.reload();
    }
  });

  // 首次安裝成功
  wb.addEventListener('activated', (event) => {
    if (!event.isUpdate) {
      console.log('[SW] 首次安裝成功，資源已預快取。');
      if (config?.onSuccess) {
        navigator.serviceWorker.getRegistration().then((registration) => {
          if (registration) {
            config.onSuccess!(registration);
          }
        });
      }
    }
  });

  wb.register()
    .then(() => {
      // 定期檢查新版本：長時間開著的頁面（SPA 不重載）也能自動偵測部署 → 自動更新重載，
      // 免使用者手動硬刷新。每 60 秒檢查一次，成本極低。
      setInterval(() => { wb.update().catch(() => {}); }, 60_000);
    })
    .catch((error) => {
      console.error('[SW] 註冊失敗:', error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config): void {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('[SW] 無法連線，應用程式以離線模式運行。');
    });
}

export function unregister(): void {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error('[SW] 取消註冊失敗:', error);
      });
  }
}
